import React from 'react';
import { useLocation } from 'react-router-dom';
import Hero from '../components/Hero';
import Summary from '../components/Summary';
import CardList from '../components/CardList';
import ContentSection from '../components/ContentSection';
import ServiceList from '../components/ServiceList';
import SEO from '../components/SEO';
import resolveImageUrl, { ResolvedImg } from '../utils/imageResolver';

import './Introduction.css';

const GridSection = ({ title, columns = 3, items = [] }) => {
  const colClass = `col-md-${12 / Math.max(1, Math.min(6, columns))}`; // support 1-6 cols
  return (
    <section className="intro-grid-section py-5">
      <div className="container">
        {title && <h2 className="text-center mb-4">{title}</h2>}
        <div className="row text-center">
          {items.map((item, idx) => (
            <div key={idx} className={`${colClass} mb-4`}>
              {item.title && <h3 className="h5">{item.title}</h3>}
              {item.description && <p>{item.description}</p>}
              {item.imageUrl && (
                <div className="intro-thumb mt-2">
                  <ResolvedImg
                    src={item.imageUrl}
                    width={360}
                    height={240}
                    text={item.title}
                    keywords={[title, item.description]}
                    alt={item.imageAlt || item.title || ''}
                    className="intro-thumb-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const mapCardBg = (bg) => (bg === 'white' ? 'white' : 'gray');

const renderCategory = (key, page, idx) => {
  if (!page) return null;

  return (
    <div key={key} className={`introduction-category introduction-${key}`}>
      {page.hero && (
        <Hero
          title={page.hero.title}
          subtitle={page.hero.subtitle}
          imageUrl={resolveImageUrl(page.hero.imageUrl, {
            width: 1200,
            height: 400,
            text: page.hero.title,
            keywords: [page.hero.subtitle]
          })}
        />
      )}

      {page.summary && (page.summary.imageUrl || page.summary.content || page.summary.sideText) && (
        <Summary
          title={page.summary.title}
          content={page.summary.content}
          imageUrl={resolveImageUrl(page.summary.imageUrl, {
            width: 500,
            height: 300,
            text: page.summary.title,
            keywords: [page.hero?.title, page.summary?.imageAlt]
          })}
          imageAlt={page.summary.imageAlt}
          sideText={page.summary.sideText}
          className="study-summary-section"
        />
      )}

      {Array.isArray(page.sections) &&
        page.sections.map((section, sidx) => {
          if (section.type === 'cardList') {
            return (
              <section key={sidx} className={`introduction-section-${idx}-${sidx}`}>
                <CardList
                  title={section.title}
                  titleDescription={section.titleDescription || ''}
                  cardListData={section.data || []}
                  body={section.body || ''}
                  backgroundColor={mapCardBg(section.backgroundColor)}
                />
              </section>
            );
          }

          if (section.type === 'grid') {
            return (
              <GridSection
                key={sidx}
                title={section.title}
                columns={section.columns}
                items={section.items || []}
              />
            );
          }

          if (section.type === 'contentSection') {
            return (
              <section key={sidx} className={`introduction-section-${idx}-${sidx}`}>
                <ContentSection
                  title={section.title}
                  subtitle={section.subtitle}
                  contentText={section.contentText}
                  contentList={section.contentList}
                  contentDisplaySide={section.contentDisplaySide || 'left'}
                  backgroundImageUrl={section.backgroundImageUrl}
                />
              </section>
            );
          }

          if (section.type === 'serviceList') {
            return (
              <section key={sidx} className={`introduction-section-${idx}-${sidx}`}>
                <ServiceList
                  title={section.title}
                  titleDescription={section.titleDescription || ''}
                  services={section.services || []}
                  backgroundColor={mapCardBg(section.backgroundColor)}
                  body={section.body || ''}
                />
              </section>
            );
          }

          return null;
        })}
    </div>
  );
};

const Introduction = ({ toDisplay, serviceData = [] }) => {
  // Use serviceData prop instead of making API call
  const services = Array.isArray(serviceData) ? serviceData : [];
  const location = useLocation();

  // When a specific service is requested (e.g., "study")
  if (toDisplay) {
    const svc = services.find((s) => s.id === toDisplay);
    if (!svc || !svc.introduction) {
      return <div className="text-center mt-5">Page not found</div>;
    }
    const intro = svc.introduction;
    const key = (svc.introduction.toURL || `/${svc.id}`)
      .replace(/^\//, '')
      .replace(/[^a-z0-9-]/gi, '') || svc.id;
    const pageTitle = intro.hero?.title || svc.label || '服務介紹';
    let pageDescription = intro.hero?.subtitle || intro.summary?.content?.substring(0, 160) || '專業留學與移民服務介紹';
    let keywords = "留學服務,移民服務,留學規劃,移民規劃";
    
    // 針對香港留學相關頁面優化關鍵詞
    if (toDisplay === 'study' || location.pathname.includes('/study')) {
      keywords = "香港留学,香港留學,香港留学申请,香港大學申請,香港研究生申請,香港副學士,香港本科,香港留學服務";
      if (!pageDescription.includes('香港')) {
        pageDescription = `香港留学申请服务 - ${pageDescription}`;
      }
    }
    
    return (
      <div className="introduction-page">
        <SEO
          title={`${pageTitle} - 留洋教育科技公司`}
          description={pageDescription}
          keywords={keywords}
          canonical={`https://liuyanghorizon.com${location.pathname}`}
          ogImage={intro.hero?.imageUrl || 'https://liuyanghorizon.com/images/logo.png'}
        />
        {renderCategory(key, intro, 0)}
      </div>
    );
  }

  // Default: render all major service introductions in a fixed order
  const order = ['study', 'hkIdentity', 'crossborder', 'techImmigration', 'investmentImmigration'];
  const servicesById = services.reduce((acc, s) => {
    acc[s.id] = s;
    return acc;
  }, {});
  const idsToRender = order.filter((id) => servicesById[id]);

  return (
    <div className="introduction-page">
      <SEO
        title="服務介紹 - 留洋教育科技公司"
        description="了解留洋教育科技公司提供的專業留學與移民服務，包括留學規劃、香港身份申請、香港生活服務、技術移民和投資移民等。"
        keywords="留學服務,移民服務,服務介紹,留學規劃,移民規劃"
        canonical={`https://liuyanghorizon.com${location.pathname}`}
        ogImage="https://liuyanghorizon.com/images/logo.png"
      />
      {idsToRender.map((id, idx) => {
        const svc = servicesById[id];
        const key = (svc.introduction?.toURL || `/${svc.id}`)
          .replace(/^\//, '')
          .replace(/[^a-z0-9-]/gi, '') || svc.id;
        return renderCategory(key, svc.introduction, idx);
      })}
    </div>
  );
};

export default Introduction;
